import BarChart from "@galaxy-io/dls/charts/BarChart";
import { ChartPalette } from "@galaxy-io/dls/charts/types";
import Box from "@galaxy-io/dls/layout/Box";
import type { AudienceIngestionDay, AudienceTrendDay } from "@gtm/contracts/core/people";
import { formatInTimeZone } from "date-fns-tz";

/**
 * Hues follow the entity, in a fixed order, so a day with no stars does not
 * repaint the comments beside it.
 */
const KINDS = {
  issue: { label: "Issues & PRs", color: ChartPalette.PURPLE },
  comment: { label: "Comments", color: ChartPalette.PINK },
  star: { label: "Stars", color: ChartPalette.BLUE },
  other: { label: "Other", color: ChartPalette.TEAL },
} as const;

const day = (value: string) => formatInTimeZone(value, "UTC", "MMM d");

/** Daily signals, stacked by the kind of record involved. */
export function ActivityChart({
  days,
  height = 200,
}: {
  height?: number;
  days: AudienceTrendDay[];
}) {
  return (
    <Box height={height}>
      <BarChart
        isFilterable
        labelFormatter={day}
        ariaLabel="Daily observed activity by kind"
        hasLegend
        placeholder="No activity in this period."
        series={{ activity: { label: "Activity" } }}
        groups={days.map((d) => ({
          label: d.day,
          bars: [
            {
              metric: "activity" as const,
              components: [
                {
                  key: "issue",
                  label: KINDS.issue.label,
                  value: d.issues,
                  color: KINDS.issue.color,
                },
                {
                  key: "comment",
                  label: KINDS.comment.label,
                  value: d.comments,
                  color: KINDS.comment.color,
                },
                { key: "star", label: KINDS.star.label, value: d.stars, color: KINDS.star.color },
                {
                  key: "other",
                  label: KINDS.other.label,
                  value: Math.max(0, d.actions - d.issues - d.comments - d.stars),
                  color: KINDS.other.color,
                },
              ],
            },
          ],
        }))}
      />
    </Box>
  );
}

/** What the collectors brought in each day: records fetched beside records that were new. */
export function IngestionChart({
  days,
  height = 200,
}: {
  height?: number;
  days: AudienceIngestionDay[];
}) {
  return (
    <Box height={height}>
      <BarChart
        isFilterable
        labelFormatter={day}
        ariaLabel="Daily ingestion: records fetched and records created"
        hasLegend
        placeholder="No collection runs in this period."
        series={{
          fetched: { label: "Fetched", color: ChartPalette.BLUE },
          created: { label: "New records", color: ChartPalette.PURPLE },
        }}
        groups={days.map((d) => ({
          label: d.day,
          bars: [
            {
              metric: "fetched" as const,
              components: [
                { key: "fetched", label: "Fetched", value: d.fetched, align: "right" as const },
              ],
            },
            {
              metric: "created" as const,
              components: [
                { key: "created", label: "New records", value: d.created, align: "right" as const },
              ],
            },
          ],
        }))}
      />
    </Box>
  );
}
